import { useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { VendorPaymentHistoryRow } from "@/api/types";

/**
 * Beat 3's history, read from the ledger.
 *
 * `lib/anomaly.ts` decides "different card than usual" by looking for the vendor's PRIOR
 * payment methods. It used to look for them in the activity feed the dashboard already had —
 * and that feed is a page of twenty-five.
 *
 * MEASURED 2026-09-08 on the booth tenant: Prairie Co-op Fuel paid twice on Visa, thirty
 * other entries filed on top, then an API restart so the feed reloaded to its window. A
 * Prairie entry on Amex produced `previouslyUsed: [Visa ··4821]` from the create endpoint —
 * the server knew — and the wall showed NOTHING. One of the three booth beats, silently dead
 * after a busy morning.
 *
 * Shaped exactly like useBalanceSheet, including the reload key, for the same reason: a booth
 * reset wipes the ledger and this must not keep describing deleted entries. Failure is
 * SILENT here by design — an empty history simply means the client falls back to what the
 * feed can see, which is the behaviour that shipped. A KPI that lies about money has to shout;
 * a beat that is merely less clever than it could be must not take the dashboard down with it.
 */
export function useVendorPaymentHistory(reloadKey: number = 0): readonly VendorPaymentHistoryRow[] {
  const [rows, setRows] = useState<readonly VendorPaymentHistoryRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load(): Promise<void> {
      try {
        const result = await demoApi.getVendorPaymentHistory(controller.signal);
        if (!cancelled) setRows(result);
      } catch {
        if (!cancelled) setRows([]);
      }
    }

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [reloadKey]);

  return rows;
}
